/* eslint-disable camelcase */
import {
  Avatar,
  Box,
  Collapse,
  Divider,
  IconButton,
  Link,
  Radio,
  styled,
} from '@material-ui/core';
import CakeIcon from '@material-ui/icons/Cake';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import HomeIcon from '@material-ui/icons/Home';
import HomeWorkIcon from '@material-ui/icons/HomeWork';
import LinkIcon from '@material-ui/icons/Link';
import LocalPhoneOutlinedIcon from '@material-ui/icons/LocalPhoneOutlined';
import StoreMallDirectoryOutlinedIcon from '@material-ui/icons/StoreMallDirectoryOutlined';
import CopyClipboardButton from '@src/components/AdminHome/Common/CopyClipboardButton';
import { ChatMessageContact } from '@src/types';
import { useEffect, useMemo, useState } from 'react';
import WspIcon from '../WspIcon';
import ChatMessageContactActions from './ChatMessageContactActions';
import usePrimaryContactPhone from './hooks/usePrimaryContactPhone';

export type ChatMessageContactCardProps = {
  contact: ChatMessageContact;
};

const ChatMessageContactCard: React.FC<ChatMessageContactCardProps> = ({
  contact,
}) => {
  const defaultPhone = usePrimaryContactPhone(contact);
  const [activePhoneIndex, setActivePhoneIndex] = useState<number>();
  const [showFullContact, setShowFullContact] = useState(false);

  const activePhone = useMemo(
    () =>
      typeof activePhoneIndex === 'number'
        ? contact.phones[activePhoneIndex]
        : defaultPhone,
    [activePhoneIndex, contact.phones, defaultPhone]
  );

  useEffect(() => {
    if (defaultPhone && !activePhoneIndex) {
      const phoneIndex = contact.phones.findIndex(
        phone => phone.wa_id === defaultPhone?.wa_id
      );
      setActivePhoneIndex(phoneIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPhone]);

  return (
    <ChatMessageContactCardStyles>
      <div className="contact-item">
        <div className="contact-item-name">
          <Avatar style={{ width: 42, height: 42 }} />
          <p style={{ fontWeight: 500 }}>
            {contact?.name?.first_name} {contact?.name?.last_name}
          </p>
        </div>

        {contact.phones?.map(({ phone, wa_id }, index) => (
          <div className="contact-item-info" key={phone}>
            {!wa_id?.length ? <LocalPhoneOutlinedIcon /> : <WspIcon />}
            {phone}

            <div className="contact-item-copy">
              <Radio
                size="small"
                color="primary"
                checked={index === activePhoneIndex}
                style={{ padding: 0, marginRight: 5 }}
                defaultChecked={wa_id === defaultPhone?.wa_id}
                onChange={() => setActivePhoneIndex(index)}
              />
              <CopyClipboardButton
                textoToClipboard={phone}
                className="contact-item-copy"
              />
            </div>
          </div>
        ))}

        {!!contact.emails?.length &&
          contact.emails?.map(({ email }) => (
            <div className="contact-item-info" key={email}>
              <EmailOutlinedIcon />
              {email}
              <CopyClipboardButton
                textoToClipboard={email}
                className="contact-item-copy"
              />
            </div>
          ))}

        {!!contact?.company?.company?.length && (
          <div className="contact-item-info">
            <StoreMallDirectoryOutlinedIcon />
            {contact.company.company}
            <CopyClipboardButton
              textoToClipboard={contact.company.company}
              className="contact-item-copy"
            />
          </div>
        )}
        <Collapse in={showFullContact}>
          <>
            {!!contact.addresses?.length &&
              contact.addresses?.map(address => {
                let fullAddress = address?.street || '';
                if (address?.city)
                  fullAddress = fullAddress.concat(`, ${address.city}`);
                if (address?.state)
                  fullAddress = fullAddress.concat(`, ${address.city}`);
                if (address?.country)
                  fullAddress = fullAddress.concat(`, ${address.country}`);
                return (
                  <div className="contact-item-info" key={fullAddress}>
                    {address.type === 'WORK' && <HomeWorkIcon />}
                    {address.type !== 'WORK' && <HomeIcon />}
                    {fullAddress}
                    <CopyClipboardButton
                      textoToClipboard={fullAddress}
                      className="contact-item-copy"
                    />
                  </div>
                );
              })}

            {!!contact?.birthday?.length && (
              <div className="contact-item-info">
                <CakeIcon />
                {contact?.birthday}
                <CopyClipboardButton
                  textoToClipboard={contact?.birthday}
                  className="contact-item-copy"
                />
              </div>
            )}

            {!!contact.urls?.length &&
              contact.urls?.map(({ url }) => (
                <div className="contact-item-info" key={url}>
                  <LinkIcon />
                  <Link href={url} target="_blank">
                    {url}
                  </Link>
                  <CopyClipboardButton
                    textoToClipboard={url}
                    className="contact-item-copy"
                  />
                </div>
              ))}
          </>
        </Collapse>

        {(!!contact.addresses?.length ||
          !!contact?.birthday?.length ||
          !!contact.urls?.length) && (
          <div className="contact-collapse-action">
            <IconButton
              size="small"
              onClick={() => setShowFullContact(!showFullContact)}
            >
              {!showFullContact ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          </div>
        )}

        <Box my={2}>
          <Divider />
        </Box>
        <div className="contact-item-actions">
          <ChatMessageContactActions
            contact={contact}
            key={activePhoneIndex}
            activePhone={activePhone}
          />
        </div>
      </div>
      <div className="contact-separator" />
    </ChatMessageContactCardStyles>
  );
};

const ChatMessageContactCardStyles = styled('div')(({ theme }) => ({
  '& .contact-item': {
    padding: '20px 25px 15px',
    '& .contact-item-name': {
      gridGap: 15,
      display: 'flex',
      marginBottom: 25,
      alignItems: 'center',
    },

    '& .contact-item-info': {
      gridGap: 8,
      display: 'flex',
      alignItems: 'center',
      fontSize: '.84rem',
      '& svg': {
        fontSize: '1.3rem',
      },
    },

    '& .contact-collapse-action': {
      svg: {
        fontSize: '1.4rem',
      },
    },

    '& .contact-item-info, & .contact-collapse-action': {
      marginTop: 5,
      '& svg': {
        color: 'inherit',
      },
    },

    '& .contact-item-actions': {
      display: 'flex',
      justifyContent: 'flex-end',
    },

    '& .contact-item-copy': {
      marginLeft: 'auto',
    },
  },
  '& .contact-separator': {
    backgroundColor:
      theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : '#F4F4F5',
    height: 15,
    width: '100%',
  },
}));

export default ChatMessageContactCard;
